import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { API, errorText, formatINR, paymentLink, quotationStatusColors } from "@/lib/payments";

const confirmCopy = {
  approve: ["Approve and lock this quotation?", "The amount will be locked and a payment link created. To change it later you must cancel and create a new quotation."],
  cancel: ["Cancel this quotation?", "Its payment link will stop accepting payments."],
};

export const QuotationCard = ({ q, authHeader, onChange }) => {
  const [confirm, setConfirm] = useState(null);
  const tid = q.number.toLowerCase();

  const act = async (action) => {
    try {
      const res = await axios.post(`${API}/admin/quotations/${q.id}/${action}`, {}, { headers: authHeader() });
      onChange(res.data);
      toast.success(action === "approve" ? "Quotation approved, payment link ready" : "Quotation cancelled");
    } catch (err) {
      toast.error(errorText(err, "Action failed"));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(paymentLink(q.public_token));
      toast.success("Payment link copied");
    } catch {
      toast.error("Copy failed. Select the link text and copy it manually.");
    }
  };

  return (
    <Card data-testid={`quotation-${tid}`}>
      <CardContent className="py-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="font-heading text-lg text-stone-900">{q.number}</h3>
            <Badge className={quotationStatusColors[q.status]} data-testid={`quotation-status-${tid}`}>{q.status.replace("_", " ")}</Badge>
          </div>
          <p className="text-sm text-stone-600">{q.customer_name}{q.company_name && ` • ${q.company_name}`} • {q.customer_email}</p>
          <p className="text-sm text-stone-500 mt-1">{q.items.length} item(s) • {new Date(q.created_at).toLocaleString()}</p>
          {q.public_token && q.status === "approved" && (
            <p className="text-xs text-stone-500 mt-2 truncate font-mono" data-testid={`quotation-link-${tid}`}>{paymentLink(q.public_token)}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xl font-heading text-stone-900 mr-2" data-testid={`quotation-amount-${tid}`}>{formatINR(q.amount_paise)}</p>
          {q.status === "draft" && (
            <Button size="sm" onClick={() => setConfirm("approve")} data-testid={`quotation-approve-btn-${tid}`}><CheckCircle2 className="h-4 w-4 mr-1" />Approve</Button>
          )}
          {q.status === "approved" && (
            <>
              <Button size="sm" variant="outline" onClick={copyLink} data-testid={`quotation-copy-link-btn-${tid}`}><Copy className="h-4 w-4 mr-1" />Copy link</Button>
              <Button size="sm" variant="ghost" asChild data-testid={`quotation-open-link-btn-${tid}`}>
                <a href={`/pay/${q.public_token}`} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /></a>
              </Button>
            </>
          )}
          {["draft", "approved"].includes(q.status) && (
            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setConfirm("cancel")} data-testid={`quotation-cancel-btn-${tid}`}><XCircle className="h-4 w-4 mr-1" />Cancel</Button>
          )}
        </div>
      </CardContent>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="quotation-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm && confirmCopy[confirm][0]}</AlertDialogTitle>
            <AlertDialogDescription>{confirm && confirmCopy[confirm][1]}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="quotation-confirm-no">Back</AlertDialogCancel>
            <AlertDialogAction onClick={() => act(confirm)} data-testid="quotation-confirm-yes">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
